import React, { useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { ChevronRightIcon, FileTextIcon, ShieldCheckIcon, StethoscopeIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { careActions, prescriptionActions, wellnessTools } from '../data/rxServices';
type LocationValue = {
  name: string;
} | null;
interface ServiceCardProps {
  title: string;
  description: string;
  path: string;
  tone: string;
  icon: React.ElementType<{
    className?: string;
    strokeWidth?: number;
  }>;
  badge?: string;
}
function ServiceCard({
  title,
  description,
  path,
  tone,
  icon: Icon,
  badge
}: ServiceCardProps) {
  const navigate = useNavigate();
  return <button type="button" onClick={() => navigate(path)} className="group flex min-h-[148px] w-full flex-col rounded-xl border border-gray-100 bg-white p-4 text-left shadow-[0_2px_10px_rgba(30,43,85,0.05)] transition-[border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-[#cbd8eb] focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:ring-offset-2" data-id="element-8144" data-mp="12j7yhc0up777">
      <div className="flex w-full items-start justify-between gap-3" data-id="element-8145" data-mp="1lbtvw0rep5tw">
        <span className={`flex h-10 w-10 items-center justify-center rounded-lg ${tone}`} data-id="element-8146" data-mp="1q9vbuctjcp15">
          <Icon className="h-5 w-5" strokeWidth={1.8} data-id="element-8147" data-mp="1bl8z8ivambdm" />
        </span>
        {badge && <span className="rounded-md bg-[#eef4ff] px-2 py-1 text-[10px] font-bold tracking-wide text-[#315b96]" data-id="element-8148" data-mp="1gjsj8dqbtd3c">{badge}</span>}
      </div>
      <div className="mt-4" data-id="element-8149" data-mp="248bt2xp0xxxj">
        <h3 className="text-sm font-bold leading-5 text-gray-900" data-id="element-8150" data-mp="24823hb7w5ssd">{title}</h3>
        <p className="mt-1 text-xs leading-5 text-gray-600" data-id="element-8151" data-mp="290v7txzg6z3x">{description}</p>
      </div>
      <span className="mt-auto flex items-center gap-1 pt-3 text-xs font-bold text-[#1E2B55]" data-id="element-8152" data-mp="2iv0q8svszz0r">
        Get started <ChevronRightIcon className="h-3.5 w-3.5 transition-transform duration-150 group-hover:translate-x-0.5" data-id="element-8153" data-mp="2dyabq9zndbhg" />
      </span>
    </button>;
}
export function RxHomePage() {
  const navigate = useNavigate();
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<LocationValue>(null);
  return <HelmetProvider data-id="element-8154" data-mp="1v5zdwujuo3q7">
      <div className="min-h-screen bg-[#f7f8fb]" data-id="element-8155" data-mp="1v5g5c4g3arou">
        <SEO title="RX Care | Prescriptions, pharmacists & health services" data-id="element-8156" data-mp="1v4o9csikyry1" />

        <div className="hidden md:block" data-id="element-8157" data-mp="1q7o2wghtjn6e">
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} showAnnouncement data-id="element-8158" data-mp="1m6cn12oqygbe" />
        </div>

        <main className="pb-24 pt-0 md:pb-0 md:pt-[var(--header-height-desktop)]" data-id="element-8159" data-mp="3nuz0achiwtxm">
          <div className="md:hidden" data-id="element-8160" data-mp="3nx30okg7rat5">
            <MobileHomeHeader selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} transparentAtTop showExperienceSwitcher activeExperience="rx" data-id="element-8161" data-mp="3stjy659az1iw" />
          </div>

          <section className="border-b border-[#ddd8f5] bg-[#f3f0ff]" data-id="element-8162" data-mp="354r0o4d18lm6">
            <div className="mx-auto max-w-7xl px-4 pb-7 pt-[132px] md:px-6 md:py-12" data-id="element-8163" data-mp="352eax51196yk">
              <div className="max-w-2xl" data-id="element-8164" data-mp="3a3tvyx37ndgy">
                <span className="inline-flex items-center gap-2 rounded-md bg-white px-3 py-1.5 text-xs font-bold text-[#684eac] ring-1 ring-[#ded3f8]" data-id="element-8165" data-mp="3e5ff306c8nzl">
                  <FileTextIcon className="h-4 w-4" data-id="element-8166" data-mp="3ix6wol7injdl" /> RX CARE
                </span>
                <h1 className="mt-4 text-3xl font-bold tracking-tight text-[#1E2B55] md:text-5xl" data-id="element-8167" data-mp="2md80046hdjnb">Your prescription, care, and wellness hub.</h1>
                <p className="mt-3 max-w-xl text-sm leading-6 text-[#4f5270] md:text-base" data-id="element-8168" data-mp="27azi3nyn701l">Upload a prescription, get trusted advice, and manage the everyday health needs that matter to you.</p>
                <div className="mt-6 flex flex-wrap gap-3" data-id="element-8169" data-mp="22a213epdcc1o">
                  <button type="button" onClick={() => navigate('/prescription')} className="inline-flex h-11 items-center gap-2 rounded-lg bg-[#1E2B55] px-5 text-sm font-bold text-white transition-colors duration-150 hover:bg-[#162041]" data-id="element-8170" data-mp="2h1kmjs0zyysf">
                    <FileTextIcon className="h-4 w-4" data-id="element-8171" data-mp="2l2ed6t5tqrp0" /> Upload prescription
                  </button>
                  <button type="button" onClick={() => navigate('/pharmacist-consultation')} className="inline-flex h-11 items-center gap-2 rounded-lg border border-[#c9c2ed] bg-white px-5 text-sm font-bold text-[#493581] transition-colors duration-150 hover:bg-[#faf9ff]" data-id="element-8172" data-mp="2zzn6vtilo7jw">
                    <StethoscopeIcon className="h-4 w-4" data-id="element-8173" data-mp="15lvz6zjtzxr3" /> Ask a pharmacist
                  </button>
                </div>
              </div>
            </div>
          </section>

          <div className="mx-auto max-w-7xl space-y-8 px-4 py-7 md:px-6 md:py-12" data-id="element-8174" data-mp="3cb7k30oywc6s">
            <section aria-labelledby="prescription-actions-heading" data-id="element-8175" data-mp="3h7xxthdauvp1">
              <div className="flex items-end justify-between gap-4" data-id="element-8176" data-mp="32kcsdgsyckdy">
                <div data-id="element-8177" data-mp="32hz0fgtkjoam">
                  <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#7155aa]" data-id="element-8178" data-mp="37iwhtr6r8eb2">I have a prescription</p>
                  <h2 id="prescription-actions-heading" className="mt-1 text-xl font-bold text-gray-900 md:text-2xl" data-id="element-8179" data-mp="2tj58w4v3ho3x">Manage your prescription</h2>
                </div>
                <button type="button" onClick={() => navigate('/prescription/history')} className="hidden text-sm font-bold text-[#1E2B55] hover:underline sm:inline-flex" data-id="element-8180" data-mp="2xink28x46ktk">View history</button>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-3 md:gap-4" data-id="element-8181" data-mp="hu3xjsm5sk9v">
                {prescriptionActions.map(action => <ServiceCard key={action.title} {...action} data-id="element-8182" data-mp="mtgvb8ekjbr8" />)}
              </div>
            </section>

            <section aria-labelledby="care-actions-heading" data-id="element-8183" data-mp="lcb7p50r06wy">
              <div data-id="element-8184" data-mp="l9f7fa1fh65c">
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#287451]" data-id="element-8185" data-mp="gcykuk9vmzud">I need help</p>
                <h2 id="care-actions-heading" className="mt-1 text-xl font-bold text-gray-900 md:text-2xl" data-id="element-8186" data-mp="bd2faglj70cp">Speak with a health professional</h2>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 md:gap-4" data-id="element-8187" data-mp="6gltht1t0y2s">
                {careActions.map(action => <ServiceCard key={action.title} {...action} data-id="element-8188" data-mp="144ca2hcqefb4" />)}
              </div>
            </section>

            <section aria-labelledby="wellness-tools-heading" data-id="element-8189" data-mp="z5iy4td3ewd2">
              <div data-id="element-8190" data-mp="ub57f49dldeq">
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#5d6482]" data-id="element-8191" data-mp="uaddryzc9pr3">Everyday wellness</p>
                <h2 id="wellness-tools-heading" className="mt-1 text-xl font-bold text-gray-900 md:text-2xl" data-id="element-8192" data-mp="pbivqz974581">Useful health tools</h2>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-3 md:gap-4" data-id="element-8193" data-mp="1i0zk9ltp31yz">
                {wellnessTools.map(tool => <ServiceCard key={tool.title} {...tool} data-id="element-8194" data-mp="1qbzz5m3r5wy5" />)}
              </div>
            </section>

            <section className="flex flex-col gap-4 rounded-xl border border-[#d6e4ef] bg-[#eff7fb] p-5 md:flex-row md:items-center md:justify-between md:p-6" data-id="element-8195" data-mp="1ud1nh7w4solc">
              <div className="flex gap-3" data-id="element-8196" data-mp="24a6epm4u3a90">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-white text-[#256a88]" data-id="element-8197" data-mp="24b96dw9zdgpz">
                  <ShieldCheckIcon className="h-5 w-5" data-id="element-8198" data-mp="297g81cvcg624" />
                </span>
                <div data-id="element-8199" data-mp="2d9kzc4ybkqll">
                  <h2 className="text-base font-bold text-gray-900" data-id="element-8200" data-mp="2d8rr0zjppt1c">Licensed care, handled securely</h2>
                  <p className="mt-1 text-sm leading-5 text-gray-600" data-id="element-8201" data-mp="2i4759slmtqya">Your prescription is reviewed by licensed professionals before it is processed.</p>
                </div>
              </div>
              <button type="button" onClick={() => navigate('/medicine-reminder')} className="h-10 flex-shrink-0 rounded-lg border border-[#a8c8d9] bg-white px-4 text-sm font-bold text-[#256a88] transition-colors duration-150 hover:bg-[#f8fdff]" data-id="element-8202" data-mp="2ry7fu9zonoee">Set medicine reminders</button>
            </section>
          </div>
        </main>

        <div className="hidden md:block" data-id="element-8203" data-mp="3rba0h0p7uzt3"><Footer data-id="element-8204" data-mp="3r9gt1j6s4bk1" /></div>
        <MobileFooterNav data-id="element-8205" data-mp="3r9ytyt4dzdya" />
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={setSelectedLocation} data-id="element-8206" data-mp="3mbnkwklx17h4" />
      </div>
    </HelmetProvider>;
}